// Client mirror of supabase/functions/_shared/ob-core/fieldRules.ts.
//
// The canonical field-behaviour matrix lives server-side (ob-core, Deno); React
// cannot import across the src/ ↔ supabase/functions boundary at runtime, so this
// is a hand-maintained mirror kept in lockstep by fieldRulesParity.test.ts — the
// same canonical-source + parity-test pattern used for scenarioRules.ts ↔
// loProspectScenarioRules.ts and artemis-pricing-status.ts ↔ pricingStatus.ts.
//
// KEEP IDENTICAL to the server module. If you change one, change both; the parity
// test fails otherwise.

export interface FieldRuleInput {
  loanType?: string;
  loanPurpose?: string;
  productTypes?: string[];
  incomeVerificationType?: string; // OB enum, e.g. 'InvestorDscr'
  lienType?: string;
  amortizationType?: string;
  interestOnly?: boolean;
  occupancy?: string;
  prepaymentPenalty?: string;
}

export interface FieldRules {
  hidden: string[];   // canonical field keys to hide in the UI (sorted)
  required: string[]; // canonical field keys that must be provided (sorted)
  notes: string[];    // human-readable rule explanations (in evaluation order)
}

export function evaluateFieldRules(s: FieldRuleInput): FieldRules {
  const hidden = new Set<string>();
  const required = new Set<string>();
  const notes: string[] = [];

  const productTypes = Array.isArray(s.productTypes) ? s.productTypes : [];
  const isEg = productTypes.includes('ExpandedGuidelines');
  // DSCR is an Expanded-Guidelines income type, so it only takes effect when EG
  // is on. Gating on isEg stops a stale InvestorDscr (e.g. after switching
  // Product Filters back to Standard) from hiding DTI or requiring the DSCR ratio.
  const isDscr = isEg && s.incomeVerificationType === 'InvestorDscr';

  // Expanded Guidelines gates the whole non-QM surface.
  if (isEg) {
    required.add('incomeVerificationType');
    required.add('monthsReserves');
  } else {
    // Non-QM inputs are irrelevant when Expanded Guidelines is off.
    for (const f of ['incomeVerificationType', 'dscrRatio', 'monthsReserves',
      'housingEvent', 'housingEventSeasoning', 'bankruptcy', 'bankruptcyOutcome',
      'bankruptcySeasoning', 'mortgageLates', 'debtConsolidation', 'uniqueProperty']) {
      hidden.add(f);
    }
  }

  // DSCR: the debt-service coverage ratio REPLACES DTI (the headline rule).
  // isDscr already implies isEg (see above), so DSCR fields are only in play when
  // Expanded Guidelines is the selected product type.
  if (isDscr) {
    hidden.add('dti');
    required.add('dscrRatio');
    required.add('monthsReserves');
  } else {
    hidden.add('dscrRatio');
  }

  // VA needs a veteran type; hide it otherwise.
  if (s.loanType === 'VA') {
required.add('veteranType');
} else {
hidden.add('veteranType');
}

  // ARM needs a fixed period.
  if (s.amortizationType === 'ARM') {
required.add('armFixedTerm');
} else {
hidden.add('armFixedTerm');
}

  // Interest-only needs a term (12–120 months, enforced in validate.ts).
  if (s.interestOnly === true) {
required.add('interestOnlyTermMonths');
} else {
hidden.add('interestOnlyTermMonths');
}

  // Second lien prices off the first-lien amount; subordinate financing is a
  // first-lien field, so a second lien must not carry secondLienAmount.
  if (s.lienType === 'Second') {
    required.add('firstLienAmount');
    hidden.add('secondLienAmount');
    notes.push('Second-lien pricing uses the first-lien amount; subordinate financing is a first-lien field.');
  } else {
    hidden.add('firstLienAmount');
  }

  // Cash-out needs an amount.
  if (s.loanPurpose === 'RefiCashout') {
required.add('cashOutAmount');
} else {
hidden.add('cashOutAmount');
}

  // Prepayment penalties only price on investment occupancy (probe-verified).
  if (s.prepaymentPenalty && s.prepaymentPenalty !== 'None' &&
      s.occupancy && s.occupancy !== 'InvestmentProperty') {
    notes.push('Prepayment penalties price only on investment properties.');
  }

  // HELOC is policy-gated (core can build/validate it, but production surfaces
  // decide whether OB will price it).
  if (s.loanType === 'HELOC') {
    notes.push('HELOC is policy-gated (OB Builder experiment; disabled on the public preview).');
  }

  // required always wins over hidden (a required field is never hidden).
  for (const r of required) {
hidden.delete(r);
}

  return {
    hidden: [...hidden].sort(),
    required: [...required].sort(),
    notes
  };
}
